import { default as classNames, default as cx } from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import dayjs from "dayjs";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CheckGreen from "static/images/CheckGreen.svg";
import MessageHighlight from "static/images/MessageHighlight.svg";
import {
  useGenerateAIResponseMutation,
  useGetUnresolvedTicketMessageIdsQuery,
} from "store/apis/chat";
import { openCreateChatTicketModal } from "store/slices/ticket";
import { RootState } from "store/store";
import {
  getWholeChar,
  MentionProps,
  MessageUIComponentProps,
  renderText,
  StreamMessage,
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
} from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import type { MessageResponse } from "stream-chat";
import { Attachment } from "./Attachment";
import { DocumentTagStatusRow } from "./DocumentTagStatusRow";
import { MessageLink } from "./MessageLink";

import { LastMessageSeenBy } from "components/chat/LastMessageSeenBy";
import { MessageThreadReplyCount } from "components/chat/MessageThreadReplyCount";
import { ReactionsList } from "components/chat/ReactionsList";
import { ChatUserName } from "components/ChatUserName/ChatUserName";
import { MegaPhone } from "components/icons/Chat/MegaPhone";
import { NotAllowed } from "components/icons/Chat/NotAllowed";
import { Pin } from "components/icons/Chat/Pin";
import { CheckOutline } from "components/icons/CheckOutline";
import { DividerLine } from "components/icons/DividerLine";
import { ForwardIcon } from "components/icons/ForwardIcon";
import { Info } from "components/icons/Info";
import { MessageDateFormat } from "components/MessageDateFormat/MessageDateFormat";
import { ThreadSlider } from "components/Thread/ThreadSlider";
import { InkleAIChatSlider } from "components/InkleAI/InkleAIChatSlider";
import {
  AI_CHANNEL,
  ANNOUNCEMENTS_CHANNEL,
  CHAT_CARD_TYPE,
  CLOSING_DOCUMENT_SUMMARY_CUSTOM_TYPE,
  CUSTOMER_FEEDBACK_CUSTOM_TYPE,
  DELETED_MESSAGE_TEXT,
  KB_SEARCH_CHANNEL,
  MESSAGE_TYPE_DELETED,
  TRANSACTIONS,
} from "constants/chatType";
import { AnimatePresence, motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useGetMessage } from "hooks/useGetMessage";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import { useModal } from "hooks/useModal";
import { useShow } from "hooks/useShow";
import { useLocation } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import SmallWhatsappIcon from "static/images/SmallWhatsappIcon.svg";
import { setDeleteMessageData } from "store/slices/chat";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { MessageCustomData } from "types/Models/Chat";
import { BackendError } from "types/utils/error";
import { RadianButton } from "../DesignSystem/RadianButton/RadianButton";
import { AIResponseFeedback } from "./AIResponseFeedback";
import { AIThinking, AIThinkingVariable } from "./AIThinking";
import { Cards } from "./Cards";
import { MessageActions } from "./MessageActions";
import { QuickReplyButton } from "./QuickReplyButton";
import { GradientButton } from "components/DesignSystem/GradientButton/GradientButton";
import Loader from "components/DesignSystem/Loader/loader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { AIAgentValidation } from "types/Models/reviewDocumentAndBalancingPayment";

const SharedOnWhatsapp = () => {
  return (
    <span className="t-flex t-gap-1 t-items-center t-mr-1 t-ml-10 t-tracking-[0.25px]">
      <span>Shared on</span>
      <img src={SmallWhatsappIcon} alt="SmallWhatsappIcon" />
      <span className="t-text-neutral-20">|</span>
    </span>
  );
};

const MessageText = ({
  message,
  sentByTheUser,
}: {
  message: MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
  };
  sentByTheUser: boolean;
}) => {
  const isDeletedMessage = message?.type === MESSAGE_TYPE_DELETED;

  const messageText: React.JSX.Element | null = useMemo(
    () =>
      renderText(message?.text, message?.mentioned_users, {
        customMarkDownRenderers: {
          mention: Mention,
          a: MessageLink,
        },
      }),
    [message?.mentioned_users, message?.text]
  );

  return (
    <span
      className={cx(
        "t-m-0 [&_p]:!t-m-0 [&_p:has(a)]:!t-block [&_p]:t-whitespace-pre-line [&_p_br]:t-hidden [&>br:has(+ul)]:t-hidden [&>br:has(+ol)]:t-hidden [&>ul+br]:t-hidden [&>ol+br]:t-hidden [&_ul]:t-my-1 [&_ul]:t-pl-5 [&_ol]:t-my-1 [&_ol]:t-pl-5 [&>p:last-child]:t-inline-block [&_code]:t-whitespace-pre-wrap",
        {
          "[&_a]:t-text-text-100": sentByTheUser,
          "t-text-text-30 t-italic": isDeletedMessage,
        }
      )}
    >
      {isDeletedMessage && (
        <span className="t-mr-1">
          <NotAllowed />
        </span>
      )}
      {isDeletedMessage ? DELETED_MESSAGE_TEXT : messageText}
    </span>
  );
};

export type MessageComponentProps = {
  actions?: ReactNode;
  message: MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
    custom_data?: {
      entity_id: string;
      group_id: string;
      action?: string;
      card_type?: string;
      entity_name?: string;
      invoice_id?: string;
      cart_item_count?: number;
      cart_item_name?: string[];
      is_sent_on_whats_app?: boolean;
      reminder_setting_id?: string;
      ai_thinking?: boolean;
      bill_id?: string;
      // quick_reply_options?: string[];
      options: {
        label: string;
        value: string;
        redirect_type: "CART" | "TASK" | "CONNECTION";
        type: "text" | "redirect";
        data?: {
          task_id?: string;
        };
      }[];
      selection?: string;
      ai_agent_validation?: AIAgentValidation;
    };
    card_status?: string;
  };
  isThreadMessage?: boolean;
  lastThreadMessage?: StreamMessage;
  otherData: { sentByTheUser: boolean };
  setQuotedMessage?: any;
  jumpToMessage?: (messageId?: string) => void;
  openTicketCreate?: (messageId: string) => void;
  mappedTicket?: Record<string, any>;
  onMessageDelete?: (message: StreamMessage) => void;
  hideOptions?: boolean;
  theme?: "light" | "dark";
  showMessageReadBy?: boolean;
  children?: ReactNode;
  isSummaryThread?: boolean;
};

export const MessageDate = ({
  sentByTheUser,
  createdAt,
  isSharedOnWhatsapp,
  isBroadCast,
}: {
  sentByTheUser?: boolean;
  createdAt?: string | Date | undefined;
  isSharedOnWhatsapp?: boolean;
  isBroadCast?: boolean;
}) => {
  const { isAdmin } = useRoleBasedView();
  const showSharedOnWhatsapp = isAdmin && isSharedOnWhatsapp;

  return (
    <>
      <span
        className={cx(
          "t-whitespace-nowrap t-text-right t-text-caption t-select-none t-text-text-30 t-h-0 t-ml-2 t-overflow-hidden t-inline-flex t-items-center",
          {
            "t-justify-end": sentByTheUser,
          }
        )}
      >
        {showSharedOnWhatsapp && <SharedOnWhatsapp />}
        {dayjs(createdAt).format("hh:mm a")}
      </span>
      <span
        className={cx(
          "t-ml-auto t-whitespace-nowrap t-text-right t-text-caption t-text-text-30 t-absolute t-right-2 t-flex t-items-center t-bottom-1",
          {
            "t-justify-end": sentByTheUser,
          }
        )}
      >
        {showSharedOnWhatsapp && <SharedOnWhatsapp />}
        {dayjs(createdAt).format("hh:mm a")}
      </span>
    </>
  );
};

const AIChecking = ({ sentByTheUser = false }: { sentByTheUser?: boolean }) => {
  return (
    <div
      className={classNames("t-flex t-items-center t-gap-1 t-px-1 t-py-0.5", {
        "t-ml-10": !sentByTheUser,
      })}
    >
      <Loader size="small" />

      <div className="t-text-caption-italic t-italic t-text-neutral-70">
        AI Agent is checking if it can assist…
      </div>
    </div>
  );
};

export const MessageLayout = (props: MessageComponentProps) => {
  const {
    message,
    otherData,
    setQuotedMessage,
    mappedTicket,
    hideOptions = false,
    children,
    showMessageReadBy,
    isThreadMessage,
    lastThreadMessage,
    isSummaryThread = false,
  } = props;

  const { search } = useLocation();
  const { sentByTheUser } = otherData || {};
  const { isAdmin, isCustomer, isCpa, canManageInternalTickets } =
    useRoleBasedView();
  const { client } = useChatContext();
  const isDeletedMessage = message?.type === MESSAGE_TYPE_DELETED;
  const pinnedByUser = client.userID === message?.pinned_by?.id;
  const isPinned = message?.pinned;
  const { alertToast } = useToast();
  const { channel } = useChannelStateContext();
  const { uuid: chatGroupId } = useCurrentChatGroupContext();
  const isLastMessage = channel.lastMessage()?.id === message?.id;
  const threadSlider = useModal();
  const aiChatSlider = useModal();
  const [generateAIResponseThread] = useGenerateAIResponseMutation();
  const { showAIChannels, showOnlyAdminMessage } = useSelector(
    (state: RootState) => state.chatSearch
  );
  const reactionMenu = useShow();
  const optionsMenu = useShow();
  const userLastMessageId = useSelector(
    (state: RootState) => state.chat.userLastMessageId
  );
  const isMyLastMessage = userLastMessageId === message?.id;

  const channelId = channel.id!;

  const onQuoteClick = () => {
    const textArea = document.getElementById("message-textarea");
    if (textArea) {
      textArea?.focus();
    }
    setQuotedMessage(message);
  };

  const generateAIResponse = async () => {
    try {
      threadSlider.open();
      await generateAIResponseThread({
        messageId: message?.id,
        channelId: channelId,
      }).unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const url = channelId ? "/settings/tickets" : "tickets";

  const customerTicketUrl = search
    ? `${url}${search}&ticketUuid=${mappedTicket?.ticket_uuid}`
    : `${url}?ticketUuid=${mappedTicket?.ticket_uuid}`;

  // Memoize threadMessage to prevent unnecessary re-renders
  const stableThreadMessage = useMemo(() => {
    if (!message?.id) return null;
    return {
      id: message.id,
      text: message.text,
      user: message.user,
      created_at: message.created_at,
      reply_count: message.reply_count,
      attachments: message.attachments,
      custom_data: message.custom_data,
    };
  }, [
    message?.id,
    message?.text,
    message?.user,
    message?.created_at,
    message?.reply_count,
    message?.attachments,
    message?.custom_data?.ai_agent_validation?.thread_feedback_rating,
  ]);

  const isKBChannel = channel?.data?.custom_type === KB_SEARCH_CHANNEL;
  const showAvatar = !sentByTheUser && message?.user && !hideOptions;
  const aiMessageViewOnly = showAIChannels && !showOnlyAdminMessage;
  const actionAllowed =
    !isDeletedMessage &&
    !hideOptions &&
    !isThreadMessage &&
    !aiMessageViewOnly &&
    !isKBChannel;
  const showMappedTicket =
    mappedTicket &&
    !mappedTicket?.is_internal_ticket &&
    (isAdmin || isCustomer || isCpa);

  const showInternalTicket =
    mappedTicket?.is_internal_ticket && canManageInternalTickets;

  const showReminderOption =
    !isCustomer && message.custom_data?.reminder_setting_id;

  const showAskAIButton =
    message?.custom_data?.ai_agent_validation?.can_get_reply_from_ai_agent &&
    isCustomer &&
    !message.reply_count &&
    !message.parent_id;

  const isLoading =
    message?.custom_data &&
    message?.custom_data?.ai_agent_validation === undefined &&
    isCustomer;

  const isAIChannel = channel?.data?.custom_type === AI_CHANNEL;
  const customData = message?.custom_data as MessageCustomData;
  const showAIThinkingForChannel =
    (isAIChannel || isKBChannel) && customData?.ai_thinking && !sentByTheUser;
  const documentTagging = customData?.document_tagging;
  return (
    <div
      data-sent={sentByTheUser}
      onDoubleClick={() => actionAllowed && onQuoteClick()}
      className={cx(
        "t-group t-flex t-flex-col t-leading-[normal] t-rounded t-relative hover:t-bg-neutral-10 t-transition-all t-duration-150 t-ease-in-out",
        {
          "t-items-end": sentByTheUser,
          "t-mt-4": isPinned,
          "t-bg-neutral-10": reactionMenu.shown || optionsMenu.shown,
        }
      )}
    >
      <div className="t-m-2">
        <div
          className={classNames("t-flex t-gap-3", {
            "t-justify-end": sentByTheUser,
          })}
        >
          {showAvatar && (
            <Avatar
              image={message?.user?.image}
              user={message?.user}
              isThreadMessage={isThreadMessage}
            />
          )}

          <div
            onDoubleClick={(e) => e.stopPropagation()}
            data-sent={sentByTheUser}
            className={classNames("t-group t-flex t-flex-col", {
              "t-gap-1": isPinned,
              "t-gap-2": !isPinned,
            })}
          >
            {isPinned && (
              <div
                className={classNames(
                  "t-flex t-items-center t-text-text-30 t-text-body-sm t-gap-1",
                  {
                    "t-justify-end": sentByTheUser,
                  }
                )}
              >
                <span className="t-text-neutral-40">
                  <Pin size="14" />
                </span>
                Pinned by{" "}
                {pinnedByUser ? "you" : <>{message?.pinned_by?.name}</>}
              </div>
            )}

            <div className="t-flex t-items-center t-gap-4 group-data-[sent=true]:t-justify-end">
              <>
                {children}
                {actionAllowed && (
                  <MessageActions
                    message={message}
                    sentByTheUser={sentByTheUser}
                    onQuoteClick={onQuoteClick}
                    channelId={channelId}
                    reactionMenu={reactionMenu}
                    optionsMenu={optionsMenu}
                    optionProps={props}
                  />
                )}
              </>
            </div>
            {!isThreadMessage && (
              <ReactionsList sentByTheUser={sentByTheUser} />
            )}

            {showMappedTicket && (
              <div
                className={classNames(
                  "t-mb-2 t-text-caption t-font-medium t-tracking-normal t-text-neutral",
                  {
                    "t-ml-9": sentByTheUser,
                    "t-text-left": !sentByTheUser,
                  }
                )}
              >
                <ConditionalLink
                  to={
                    isAdmin
                      ? `/tickets?ticketUuid=${mappedTicket?.ticket_uuid}`
                      : customerTicketUrl
                  }
                  className={classNames("t-text-text-100 ", {
                    "hover:t-text-purple": !sentByTheUser,
                  })}
                >
                  {mappedTicket?.resolved ? (
                    <div
                      className={classNames("t-text-black-30 t-flex t-gap-1", {
                        "t-justify-end": sentByTheUser,
                      })}
                    >
                      <img
                        src={CheckGreen}
                        alt="MessageHighlight"
                        className="t-select-none"
                      />
                      Ticket resolved
                    </div>
                  ) : (
                    <div
                      className={classNames("t-text-black-30 t-flex t-gap-1", {
                        "t-justify-end": sentByTheUser,
                      })}
                    >
                      <img
                        src={MessageHighlight}
                        alt="MessageHighlight"
                        className="t-select-none"
                      />
                      Ticket created
                    </div>
                  )}
                </ConditionalLink>
              </div>
            )}

            {showInternalTicket && (
              <div
                className={classNames(
                  "t-mb-2 t-text-caption t-font-medium t-tracking-normal t-text-neutral",
                  {
                    "t-ml-9": sentByTheUser,
                    "t-text-left": !sentByTheUser,
                  }
                )}
              >
                <ConditionalLink
                  to={`/tickets?ticketUuid=${mappedTicket?.ticket_uuid}`}
                  className={classNames("t-text-text-100", {
                    "hover:t-text-purple": !sentByTheUser,
                  })}
                >
                  <div
                    className={classNames("t-text-black-30 t-flex t-gap-1", {
                      "t-justify-end": sentByTheUser,
                    })}
                  >
                    <img
                      src={MessageHighlight}
                      alt="MessageHighlight"
                      className="t-select-none"
                    />
                    Internal ticket created
                  </div>
                </ConditionalLink>
              </div>
            )}

            {/* @ts-ignore */}
            {showReminderOption && (
              <div
                className={classNames(
                  "t-mb-2 t-text-caption t-font-medium t-tracking-normal t-text-neutral",
                  {
                    "t-ml-9": sentByTheUser,
                    " t-text-left": !sentByTheUser,
                  }
                )}
              >
                <div
                  className={classNames(
                    "t-text-black-30 t-flex t-gap-1 t-items-center",
                    {
                      "t-justify-end": sentByTheUser,
                    }
                  )}
                >
                  <span className="t-flex t-items-center">
                    <CheckOutline />
                  </span>
                  Reminder set
                </div>
              </div>
            )}
          </div>
        </div>
        {isLastMessage && sentByTheUser && !isThreadMessage && (
          <div
            className={classNames("t-mt-1", {
              "t-absolute t-bottom-0":
                message.reaction_counts &&
                Object.entries(message.reaction_counts).length > 0 &&
                sentByTheUser,
              // "t-ml-[72px]": sentByTheUser,
            })}
          >
            <Async.Root
              isLoading={Boolean(isLoading)}
              isSuccess={Boolean(showAskAIButton)}
              isEmpty={false}
              renderOnLoad={<AIChecking sentByTheUser={sentByTheUser} />}
            >
              <Async.Success>
                <GradientButton
                  onClick={generateAIResponse}
                  className={classNames({
                    "t-ml-10": !sentByTheUser,
                  })}
                >
                  Check what AI says
                </GradientButton>
              </Async.Success>
            </Async.Root>
          </div>
        )}
        {Boolean(message?.custom_data) && (
          <div className={classNames({ "t-ml-10": !sentByTheUser })}>
            <DocumentTagStatusRow
              documentTagging={documentTagging}
              messageId={message.id!}
              channelId={channelId}
              groupId={
                (channel?.data?.group_uuid as string) || chatGroupId || ""
              }
            />
          </div>
        )}
        <div className="t-relative">
          {Boolean(
            !isThreadMessage &&
              !isKBChannel &&
              message?.reply_count &&
              message.reply_count > 0
          ) && (
            <MessageThreadReplyCount
              threadParticipants={(message?.thread_participants || []).map(
                (participant) => ({
                  name: participant.name || "Unknown",
                  profile_url: participant.image || "",
                  image: participant.image || "",
                  id: participant.id,
                })
              )}
              replyCount={message.reply_count!}
              openThreadSlider={
                showAIChannels ? aiChatSlider.open : threadSlider.open
              }
              sentByTheUser={sentByTheUser}
            />
          )}
          {isThreadMessage && !sentByTheUser && (
            <AIResponseFeedback message={message} channelId={channelId} />
          )}
          {threadSlider.isOpen && (
            <ThreadSlider
              open={threadSlider.isOpen}
              onClose={threadSlider.close}
              threadMessage={stableThreadMessage}
            />
          )}
          {aiChatSlider.isOpen && (
            <InkleAIChatSlider
              open={aiChatSlider.isOpen}
              onClose={aiChatSlider.close}
              aiChannelId={channelId}
              currentEntityId={channel.data?.entity_id as string}
              shouldUpdateQuery={false}
              initialThreadMessage={
                stableThreadMessage as unknown as MessageResponse<DefaultStreamChatGenerics>
              }
            />
          )}
        </div>
      </div>
      {isThreadMessage &&
        sentByTheUser &&
        lastThreadMessage?.id === message?.id && (
          <div className="t-absolute t-left-0 -t-bottom-10">
            {showAIThinkingForChannel ? (
              <AIThinkingVariable />
            ) : (
              <AIThinking isSummaryThread={isSummaryThread} />
            )}
          </div>
        )}
      {showMessageReadBy && isAdmin && isLastMessage && (
        <LastMessageSeenBy
          messageId={message?.id}
          messageCreatedAt={message.created_at}
        />
      )}
    </div>
  );
};

export const MessageBody = (
  props: MessageComponentProps & { Date?: ReactNode }
) => {
  const {
    message,
    otherData,
    jumpToMessage,
    theme = "dark",
    children,
    Date,
    actions,
    isThreadMessage,
  } = props;
  const { client } = useChatContext();
  const { sentByTheUser } = otherData;
  const {
    attachments,
    quoted_message,
    custom_data: customData,
  } = message || {};
  const messageToScrollTo = useSelector(
    (state: RootState) => state.messageToScrollTo.messageId
  );
  const { channel } = useChannelStateContext();
  const scrolledToThisMessage = messageToScrollTo === message?.id;
  const isPinned = message?.pinned;
  const isAnnouncementChannel =
    channel?.data?.custom_type === ANNOUNCEMENTS_CHANNEL;
  const isSharedOnWhatsapp = (message?.custom_data as MessageCustomData)
    ?.is_sent_on_whats_app;

  const parentId = message?.parent_id;

  const parentMessage = parentId
    ? channel.state.messages.find((m) => m.id === parentId)
    : undefined;

  // @ts-ignore
  const entityId = parentMessage?.custom_data?.entity_id || "";
  const { uuid: chatGroupId } = useCurrentChatGroupContext();

  return (
    <div className="t-order-1 t-text-body-sm group-data-[sent=true]:t-order-2">
      <div
        className={cx(
          "t-hyphens-auto t-relative  t-flex-col t-gap-2.5 t-word-break t-transition-all t-duration-300",
          {
            "!t-bg-orange-10": isPinned,
            "t-rounded-lg t-rounded-tr-sm t-bg-purple-10 t-text-purple-100":
              sentByTheUser,
            "t-rounded-lg t-rounded-tl-sm t-text-text-60": !sentByTheUser,
            "t-p-3 t-px-2.5": (attachments?.length || 0) > 0,
            "t-p-2 t-pt-1.5": (attachments?.length || 0) === 0,
            "t-animate-highlightReceivedChatMessage":
              scrolledToThisMessage && !sentByTheUser,
            "t-animate-highlightSentChatMessage":
              scrolledToThisMessage && sentByTheUser,
            "t-bg-yellow-10": isAnnouncementChannel,
            "t-max-w-[var(--message-max-width)]":
              message?.custom_type !== CHAT_CARD_TYPE && !isThreadMessage,
            "t-max-w-[var(--chat-card-max-width,306px)]":
              message?.custom_type === CHAT_CARD_TYPE,
            "t-bg-white": !sentByTheUser && theme === "dark" && !isPinned,
            "t-bg-surface-grey":
              !sentByTheUser && theme === "light" && !isPinned,
            "t-max-w-[306px] t-ml-auto": isThreadMessage,
          }
        )}
      >
        <div className="t-flex">
          <div>
            {!sentByTheUser && (
              <p className="t-m-0 t-mb-1 t-text-subtext-sm t-text-text-100 t-hidden group-[&]/top:t-flex group-[&]/single:t-flex t-select-none">
                <ChatUserName
                  role={message?.user?.role_name}
                  name={message?.user?.name}
                />
              </p>
            )}
            <div className="t-flex t-flex-col t-gap-2.5">
              {quoted_message?.id && (
                <button
                  onClickCapture={() => jumpToMessage?.(quoted_message.id)}
                  className="all:unset t-flex t-flex-col t-gap-1 t-rounded t-border-solid t-border-l-4 t-border-0 t-bg-surface-lighter-grey t-p-2 t-border-neutral-40 group-data-[sent=true]:t-text-text-60"
                >
                  <span className="t-text-subtext-sm t-text-neutral-50 t-flex t-items-center">
                    {quoted_message?.user?.id === client?.user?.id ? (
                      "You"
                    ) : (
                      <ChatUserName
                        role={quoted_message?.user?.role_name}
                        name={quoted_message?.user?.name}
                      />
                    )}
                  </span>
                  <fieldset
                    disabled
                    className="t-m-0 t-border-0 t-p-0 t-pointer-events-none [&_*]:!t-text-neutral-40 [&_*]:!t-shadow-none [&>div>div]:!t-bg-surface-lighter-grey [&_button]:!t-outline-none [&_button_.t-absolute]:!t-hidden [&_.t-select-none]:!t-hidden"
                  >
                    <MessageBody
                      // @ts-ignore
                      message={{
                        ...quoted_message,
                        quoted_message: undefined,
                      }}
                      otherData={{ sentByTheUser: false }}
                    />
                  </fieldset>
                </button>
              )}

              {Boolean(attachments?.length) && (
                <Attachment attachments={attachments!} sent={sentByTheUser} />
              )}

              {(message?.custom_data || message.text || children) && (
                <div
                  className={classNames({
                    "t-mb-2 t-flex t-flex-col t-gap-2":
                      Boolean(message?.custom_data) && !isSharedOnWhatsapp,
                  })}
                >
                  {(message.custom_type !== TRANSACTIONS ||
                    message?.type === MESSAGE_TYPE_DELETED) && (
                    <MessageText
                      message={message}
                      sentByTheUser={sentByTheUser}
                    />
                  )}
                  {Boolean(message?.custom_data) && (
                    <Cards
                      type={message?.custom_type as string}
                      customData={message?.custom_data}
                      messageId={message?.id!}
                      card_status={message?.card_status || ""}
                      entityId={entityId || ""}
                      groupId={
                        (channel?.data?.group_uuid as string) ||
                        chatGroupId ||
                        ""
                      }
                    />
                  )}
                  {message.custom_type === TRANSACTIONS &&
                    Boolean(message.text?.trim()) && (
                      <MessageText
                        message={message}
                        sentByTheUser={sentByTheUser}
                      />
                    )}
                  {children}
                </div>
              )}
            </div>
          </div>
        </div>
        {Boolean(message?.is_broadcast) && <BroadcastBanner />}
        {Date ? Date : null}
      </div>
      {actions}
    </div>
  );
};

const BroadcastBanner = () => {
  return (
    <div className="t-flex t-flex-row t-mt-4 t-items-center t-gap-x-2 t-bg-surface-lighter-grey t-p-2 t-rounded-md">
      <span className="t-text-neutral-40">
        <MegaPhone />
      </span>
      <p className="t-text-text-30 t-text-body-sm t-mb-0">
        This is a broadcast message, ignore if already done
      </p>
    </div>
  );
};

export const MessageBubble = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>() => {
  const { message } = useMessageContext();
  const { sendbirdCreds: chatCreds, authtoken } = useContext(authContext);
  const { setQuotedMessage, jumpToMessage } =
    useChannelActionContext<StreamChatGenerics>("MessageActionsBox");
  const dispatch = useDispatch();
  const { channel } = useChannelStateContext();
  const { isAdmin, isCustomer, isCpa } = useRoleBasedView();
  const chatMemberId = chatCreds?.member_id || authtoken?.member?.member_id;

  // Check if the message was sent by the current user
  const isSentByCurrentUser = message.user?.id === chatMemberId;
  const sentByTheUser = isSentByCurrentUser;
  const { data: unresolvedTickets } = useGetUnresolvedTicketMessageIdsQuery(
    {
      channelUrl: channel?.id!,
    },
    {
      skip: (!isAdmin && !isCustomer && !isCpa) || !channel?.id,
    }
  );

  const ticketMap: Record<string, any> = unresolvedTickets
    ? unresolvedTickets.ticket_messages?.reduce((acc, e) => {
        return {
          ...acc,

          [e?.message_id]: e,
        };
      }, {})
    : {};

  const openTicketCreate = (messageId: string) => {
    dispatch(
      openCreateChatTicketModal({
        messageId,
        showCreateTicketModal: true,
        channelId: channel.id || "",
        isInternalTicket: false,
      })
    );
  };

  const onMessageDelete = (message: StreamMessage) => {
    dispatch(
      setDeleteMessageData({
        message,
        showDeleteMessageModal: true,
        channelId: channel.id || "",
      })
    );
  };

  return (
    <MessageBubbleUI
      setQuotedMessage={setQuotedMessage}
      otherData={{ sentByTheUser }}
      message={message}
      jumpToMessage={jumpToMessage}
      openTicketCreate={openTicketCreate}
      mappedTicket={ticketMap[message?.id]}
      onMessageDelete={onMessageDelete}
    />
  );
};

export const Mention = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  children,
  node: { mentionedUser },
}: MentionProps<StreamChatGenerics>) => (
  <span
    className="t-rounded-sm t-p-0.5 t-text-purple group-data-[sent=true]:t-bg-white"
    data-user-id={mentionedUser.id}
  >
    {children}
  </span>
);

export const Avatar = ({
  image,
  user,
  isThreadMessage,
}: {
  image?: string;
  user?: { name?: string } | null;
  isThreadMessage?: boolean;
}) => {
  if (image) {
    return (
      <img
        src={image}
        alt="Pic"
        className={classNames(
          "t-h-7 t-w-7 t-rounded-full t-opacity-0 group-[&]/top:t-opacity-100 group-[&]/single:t-opacity-100 t-select-none",
          {
            "!t-opacity-100": isThreadMessage,
          }
        )}
      />
    );
  }

  if (user?.name) {
    return (
      <div
        className={classNames(
          "t-flex t-h-7 t-w-7 t-items-center t-opacity-0 group-[&]/top:t-opacity-100 t-justify-center t-rounded-full t-bg-purple-20 t-text-text-60 group-[&]/single:t-opacity-100 t-select-none t-flex-shrink-0",
          {
            "!t-opacity-100": isThreadMessage,
          }
        )}
      >
        <span>{getWholeChar(user.name?.toString(), 0)}</span>
      </div>
    );
  }

  return null;
};

const ForwardMessage = (props: {
  message: MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
    customData?: MessageCustomData;
  };
  otherData: any;
  setQuotedMessage?: any;
  jumpToMessage?: any;
  openTicketCreate?: (messageId: string) => void;
  mappedTicket?: Record<string, any>;
  onMessageDelete?: (message: StreamMessage) => void;
  hideOptions?: boolean;
  theme?: "light" | "dark";
  isThreadMessage?: boolean;
  lastThreadMessage?: StreamMessage;
}) => {
  const {
    message,
    otherData: { sentByTheUser },
    theme = "dark",
    isThreadMessage,
  } = props;

  const { channel } = useChannelStateContext();
  const messageToScrollTo = useAppSelector(
    (store) => store.messageToScrollTo.messageId
  );

  const dispatch = useDispatch();

  const channelUrl = channel?.id as string;
  const forwardedMessage = useGetMessage(
    message?.forwarded_message?.id!,
    message?.id,
    channelUrl
  );

  const scrolledToThisMessage = messageToScrollTo === message?.id;
  const isPinned = message?.pinned;
  const isSharedOnWhatsapp = (message?.custom_data as MessageCustomData)
    ?.is_sent_on_whats_app;

  const isAnnouncementChannel =
    channel?.data?.custom_type === ANNOUNCEMENTS_CHANNEL;

  if (message.forwarded_message && forwardedMessage) {
    return (
      <motion.div initial={{ y: 20 }} animate={{ y: -0 }}>
        <MessageLayout {...props} showMessageReadBy>
          <div
            className={cx(
              "t-hyphens-auto t-relative t-order-1 t-flex-col t-gap-2.5 t-word-break t-text-body-sm t-transition-all t-duration-300 group-data-[sent=true]:t-order-2",
              {
                "!t-bg-orange-10": isPinned,
                "t-rounded-lg t-rounded-tr-sm t-bg-purple-10 t-text-purple-100":
                  sentByTheUser,
                "t-rounded-lg t-rounded-tl-sm t-text-text-60": !sentByTheUser,
                "t-p-3 t-px-2.5": (message.attachments?.length || 0) > 0,
                "t-p-2 t-pt-1.5": (message.attachments?.length || 0) === 0,
                "t-animate-activate": scrolledToThisMessage,
                "t-bg-yellow-10": isAnnouncementChannel,
                "t-max-w-[var(--message-max-width)]":
                  message?.custom_type !== CHAT_CARD_TYPE && !isThreadMessage,
                "t-max-w-[var(--chat-card-max-width,306px)]":
                  message?.custom_type === CHAT_CARD_TYPE,
                "t-bg-white": !sentByTheUser && theme === "dark" && !isPinned,
                "t-bg-surface-grey":
                  !sentByTheUser && theme === "light" && !isPinned,
                "t-max-w-[306px] t-ml-auto": isThreadMessage,
              }
            )}
          >
            <div className="t-flex t-mb-2 t-gap-2 t-mr-5 t-items-center">
              <p className="all:unset t-flex t-mb-0 t-gap-1 t-text-body-sm t-text-text-60 t-items-center">
                <ForwardIcon />
                <span className="t-text-subtext-sm">
                  {sentByTheUser ? "You" : message.user?.name}
                </span>{" "}
                <span className="t-text-text-30">forwarded this message</span>
              </p>
            </div>
            <div className="t-flex">
              <div className="t-border-l-4 t-border-0 t-border-solid t-rounded t-border-l-purple-30 t-border-neutral-10">
                {/* @ts-ignore */}
                <MessageBody
                  {...props}
                  jumpToMessage={(id) => dispatch(setScrollToMessageId(id))}
                  message={forwardedMessage}
                />

                <ConditionalLink
                  to={`/chat/${forwardedMessage?.cid?.replace(
                    "messaging:",
                    ""
                  )}/${forwardedMessage?.id}`}
                  className="t-px-2 t-pb-1.5 t-text-[10px] t-text-text-30 hover:!t-underline t-flex t-gap-1 t-items-center t-mt-1.5"
                >
                  <MessageDateFormat createdAt={forwardedMessage?.created_at} />
                  <span className="t-text-neutral-20">
                    <DividerLine />
                  </span>
                  <span>View message</span>
                </ConditionalLink>
              </div>
            </div>
            <span className="t-mt-2 t-inline-block">
              <MessageText message={message} sentByTheUser={sentByTheUser} />
            </span>
            <MessageDate
              sentByTheUser={sentByTheUser}
              createdAt={message.created_at}
              isSharedOnWhatsapp={isSharedOnWhatsapp}
              isBroadCast={Boolean(message?.is_broadcast)}
            />
          </div>
        </MessageLayout>
      </motion.div>
    );
  }

  return null;
};

export const MessageTypeHelper = ({ message }: { message: StreamMessage }) => {
  if (message.custom_type === CLOSING_DOCUMENT_SUMMARY_CUSTOM_TYPE) {
    return (
      <div className="t-flex t-mb-2 t-rounded-lg t-gap-2 t-items-center">
        <span className="t-flex t-shrink-0 t-self-start t-text-blue-80">
          <Info color="currentColor" stroke="1.2" size="20" />
        </span>
        <div className="t-space-y-1">
          <p className="t-text-body-sm t-text-blue-80 t-mb-0 t-italic">
            {"This is an AI generated summary"}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

export const MessageBubbleUI = (props: {
  message: MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
  };
  otherData: any;
  setQuotedMessage?: any;
  jumpToMessage?: any;
  openTicketCreate?: (messageId: string) => void;
  mappedTicket?: Record<string, any>;
  onMessageDelete?: (message: StreamMessage) => void;
  hideOptions?: boolean;
  theme?: "light" | "dark";
  isThreadMessage?: boolean;
  lastThreadMessage?: StreamMessage;
  isSummaryThread?: boolean;
}) => {
  const {
    message,
    otherData: { sentByTheUser },
    isSummaryThread = false,
  } = props;

  const dispatch = useDispatch();
  const { channel } = useChannelStateContext();
  const isV2Platform = useIsGroupV2Platform();

  const customData = message?.custom_data as MessageCustomData;

  const isDeletedMessage = message?.type === MESSAGE_TYPE_DELETED;
  const isSharedOnWhatsapp = customData?.is_sent_on_whats_app;
  const isAIChannel = channel?.data?.custom_type === AI_CHANNEL;
  const isKBChannel = channel?.data?.custom_type === KB_SEARCH_CHANNEL;
  const shouldShowOnlyLoader = isV2Platform
    ? customData?.ai_thinking && !sentByTheUser
    : (isAIChannel || isKBChannel) && customData?.ai_thinking && !sentByTheUser;

  if (message.forwarded_message && !isDeletedMessage) {
    return <ForwardMessage {...props} />;
  }

  if (shouldShowOnlyLoader) {
    if (isDeletedMessage) return null;

    return (
      <motion.div initial={{ y: 20 }} animate={{ y: -0 }}>
        <div>
          <AIThinkingVariable statusText={customData?.status_text} />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ y: 20 }} animate={{ y: -0 }}>
      <>
        <MessageTypeHelper message={message} />
        <MessageLayout
          {...props}
          isSummaryThread={isSummaryThread}
          showMessageReadBy
        >
          <MessageBody
            {...props}
            jumpToMessage={(id) => dispatch(setScrollToMessageId(id))}
            Date={
              <MessageDate
                sentByTheUser={sentByTheUser}
                createdAt={message.created_at}
                isSharedOnWhatsapp={isSharedOnWhatsapp}
                isBroadCast={Boolean(message?.is_broadcast)}
              />
            }
            actions={
              customData?.options &&
              customData.options.length > 0 && (
                <div
                  className={cx(
                    "t-flex t-flex-wrap t-gap-2 t-mt-2 t-flex-col",
                    {
                      "t-justify-end": sentByTheUser,
                    }
                  )}
                >
                  {customData.options.map((option, index: number) => (
                    <QuickReplyButton
                      key={`${option.value}-${index}`}
                      label={option.label}
                      value={option.value}
                      type={option.type}
                      redirectType={option.redirect_type}
                      taskId={option.data?.task_id}
                      messageId={message?.id}
                      sentByTheUser={sentByTheUser}
                      selectedValue={customData.selection}
                    />
                  ))}
                </div>
              )
            }
          />
        </MessageLayout>
      </>
    </motion.div>
  );
};

export const ThreadMessageBubble = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  message,
  isThreadMessage = false,
  lastThreadMessage,
  isSummaryThread = false,
}: {
  message: StreamMessage;
  isThreadMessage?: boolean;
  lastThreadMessage?: StreamMessage;
  isSummaryThread?: boolean;
}) => {
  // const { message } = useMessageContext();
  const { sendbirdCreds: chatCreds, authtoken } = useContext(authContext);
  const { setQuotedMessage, jumpToMessage } =
    useChannelActionContext<StreamChatGenerics>("MessageActionsBox");
  const dispatch = useDispatch();
  const { channel } = useChannelStateContext();
  const { isAdmin, isCustomer, isCpa } = useRoleBasedView();
  const chatMemberId = chatCreds?.member_id || authtoken?.member?.member_id;

  // Check if the message was sent by the current user
  const isSentByCurrentUser = message.user?.id === chatMemberId;
  const sentByTheUser = isSentByCurrentUser;
  const { data: unresolvedTickets } = useGetUnresolvedTicketMessageIdsQuery(
    {
      channelUrl: channel?.id!,
    },
    {
      skip: (!isAdmin && !isCustomer && !isCpa) || !channel?.id,
    }
  );

  const ticketMap: Record<string, any> = unresolvedTickets
    ? unresolvedTickets.ticket_messages?.reduce((acc, e) => {
        return {
          ...acc,

          [e?.message_id]: e,
        };
      }, {})
    : {};

  const openTicketCreate = (messageId: string) => {
    dispatch(
      openCreateChatTicketModal({
        messageId,
        showCreateTicketModal: true,
        channelId: channel.id || "",
        isInternalTicket: false,
      })
    );
  };

  const onMessageDelete = (message: StreamMessage) => {
    dispatch(
      setDeleteMessageData({
        message,
        showDeleteMessageModal: true,
        channelId: channel.id || "",
      })
    );
  };

  return (
    <MessageBubbleUI
      setQuotedMessage={setQuotedMessage}
      otherData={{ sentByTheUser }}
      jumpToMessage={jumpToMessage}
      message={message}
      openTicketCreate={openTicketCreate}
      mappedTicket={ticketMap[message?.id]}
      onMessageDelete={onMessageDelete}
      isThreadMessage={isThreadMessage}
      lastThreadMessage={lastThreadMessage}
      isSummaryThread={isSummaryThread}
    />
  );
};
